import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { catchError, finalize, tap } from 'rxjs/operators';
import { BsModalRef, BsModalService, ModalOptions } from 'ngx-bootstrap/modal';

import { IdentityType } from './otp.enum';

import { OtpGenerateRes, OtpLoginRes, OtpValidateData, OtpValidateRes } from './otp.model';
import { Credential } from '@model/authentication.model';

import { AuthenticationService } from '@service/authentication.service';
import { OtpService } from './otp.service';

@Component({
  selector: 'app-otp',
  templateUrl: './otp.component.html',
  styleUrls: ['./otp.component.scss']
})
export class OtpComponent implements OnInit {
  otpForm: UntypedFormGroup;
  mobileNumber: string;
  requestId: string;
  errorMessage: string;
  refId: string;

  identityType: string;

  cooldownSec: number;
  timer: any;

  isEditIdInfo = true;
  showOtpTab = false;
  isLoading = false;
  disallowIdEdit = false;

  modalRef: BsModalRef;
  @ViewChild('errorModal') errorModal: TemplateRef<any>;

  constructor(
    private router: Router,
    private formBuilder: UntypedFormBuilder,
    private modalService: BsModalService,
    private translateService: TranslateService,
    private authenticationService: AuthenticationService,
    private otpService: OtpService
  ) {}

  ngOnInit(): void {
    this.authenticationService.clearCurrentUser();
    this.otpForm = this.getOtpForm();
  }

  getMobileNumber(): void {
    if (!this.identityInfo.value.trim()) {
      return;
    }
    this.isLoading = true;

    this.identityType = this.identityInfo.value.length === 13 ? IdentityType.IDCARD : IdentityType.PASSPORT;

    this.otpService
      .getMobileNo(this.identityInfo.value)
      .pipe(tap((data: OtpLoginRes) => {
          this.mobileNumber = data.mobileNo;
          this.requestId = data.requestId;
          this.errorMessage = null;
        }),
        catchError((e: HttpErrorResponse) => {
          this.mobileNumber = null;
          this.requestId = null;
          const errorCode: string = e.error && e.error.code ? e.error.code : '';
          this.handleError(errorCode);
          throw e;
        }),
        finalize(() => {
          this.isLoading = false;
          this.isEditIdInfo = false;
        })
      )
      .subscribe();
  }

  editIdentityInfo(): void {
    this.isEditIdInfo = true;
    this.mobileNumber = null;
  }

  identityInfoChange(): void {
    this.identityInfo.setValue(this.identityInfo.value.toUpperCase().replace(/\s/g, ''));
  }

  requestOtp(): void {
    this.isLoading = true;

    this.otpService
      .generateOtp(this.requestId, this.translateService.currentLang, this.refId)
      .pipe(tap((data: OtpGenerateRes) => {
        this.errorMessage = null;

        this.refId = data.referenceId;
        this.showOtpTab = true;
        this.cooldownRequestOtp();
      }),
      catchError((e: HttpErrorResponse) => {
        const errorCode: string = e.error && e.error.code === '113' ? e.error.code : 'OTPGEN';
        this.handleError(errorCode);
        throw e;
      }),
      finalize(() => {
        this.isLoading = false;
      }))
      .subscribe();
  }

  confirmOtp(): void {
    this.isLoading = true;

    const data: OtpValidateData = {
      requestId: this.requestId,
      otp: this.otp.value,
      otpReferenceId: this.refId
    };

    this.otpService
      .validateOtp(data)
      .pipe(tap((validateRes: OtpValidateRes) => {
        this.setCredential(validateRes);
        this.router.navigate(['document/list']);
      }),
      catchError((e: HttpErrorResponse) => {
        const errorCode: string = e.error && e.error.code ? e.error.code : '';
        this.handleError(errorCode);
        throw e;
      }),
      finalize(() => {
        this.isLoading = false;
      }))
      .subscribe();
  }

  redirectLandingPage(): void {
    window.location.reload();
  }

  get identityInfo(): AbstractControl {
    return this.otpForm.get('identityInfo');
  }

  get otp(): AbstractControl {
    return this.otpForm.get('otp');
  }

  private setCredential(validateRes: OtpValidateRes): void {
    const credential: Credential = {
      identityType: this.identityType,
      identityNo: this.identityInfo.value,
      accessToken: validateRes.accessToken,
      referenceId: validateRes.referenceId
    };
    this.authenticationService.setCurrentCredential(credential);
  }

  private getOtpForm(): UntypedFormGroup {
    return this.formBuilder.group({
      identityInfo: ['', [Validators.required]],
      otp: ['', [Validators.required, Validators.minLength(6)]]
    });
  }

  private handleError(errorCode: string): void {
    switch (errorCode) {
      case '109':
        this.errorMessage = 'otp.errors.invalid';
        break;
      case '110':
        this.errorMessage = 'otp.errors.idNotFound';
        break;
      case '111':
        this.errorMessage = 'otp.errors.moblieNotFound';
        break;
      case '113':
        this.errorMessage = 'otp.errors.moblieDisallow';
        this.disallowIdEdit = true;
        break;
      case '114':
        this.openErrorModal();
        break;
      case 'OTPGEN':
        this.errorMessage = 'otp.errors.generate';
        break;
      default:
        this.errorMessage = 'errors.unexpected';
    }
  }

  private cooldownRequestOtp(): void {
    clearInterval(this.timer);
    this.cooldownSec = 15;
    this.timer = setInterval(() => {
      this.cooldownSec = this.cooldownSec - 1;
      if (!this.cooldownSec || this.cooldownSec <= 0) {
        clearInterval(this.timer);
      }
    }, 1000);
  }

  private openErrorModal(): void {
    const modalOptions: ModalOptions = {
      backdrop: 'static',
      ignoreBackdropClick: true,
      class: 'modal-dialog-centered'
    };
    this.modalRef = this.modalService.show(this.errorModal, modalOptions);
  }
}
